import * as THREE from 'three'
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js'
import type { TransitionConfig, TransitionDirection, TransitionType } from '../types'

/** Transition style → shader branch id (kept in sync with the `switch` below). */
const TX_CODE: Record<TransitionType, number> = {
  crossfade: 0,
  dip: 1,
  lumaWipe: 2,
  bars: 3,
  iris: 4,
  pixelate: 5,
  glitch: 6,
  rgbShift: 7,
  zoomBlur: 8,
  whipPan: 9,
  displace: 10,
  ripple: 11,
  burn: 12,
  spin: 13,
}

/** Direction → unit vector on screen (y up). */
function dirVec(d: TransitionDirection): [number, number] {
  switch (d) {
    case 'left':
      return [-1, 0]
    case 'right':
      return [1, 0]
    case 'up':
      return [0, 1]
    case 'down':
      return [0, -1]
    case 'in':
      return [1, 0]
    case 'out':
      return [-1, 0]
  }
}

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`

// Mixes the OUTGOING look (tFrom) into the INCOMING look (tTo) by uProgress.
// One big switch on uType implements the whole transition pool. Self-contained
// (procedural hash/noise, no texture deps). The fast effect-driven styles mask
// the cut with motion; the rest are tasteful simple wipes/fades.
const FRAG = /* glsl */ `
  precision highp float;
  uniform sampler2D tFrom;
  uniform sampler2D tTo;
  uniform float uProgress;   // 0..1 raw
  uniform int   uType;
  uniform float uIntensity;  // ~0..1.5
  uniform float uAspect;     // width / height
  uniform float uTime;       // seconds (glitch animation)
  uniform float uSeed;       // per-transition random
  uniform vec2  uDir;        // direction unit vector
  varying vec2 vUv;

  const float PI = 3.14159265;

  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 345.45));
    p += dot(p, p + 34.345);
    return fract(p.x * p.y);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float a = hash21(i), b = hash21(i + vec2(1.0, 0.0));
    float c = hash21(i + vec2(0.0, 1.0)), d = hash21(i + vec2(1.0, 1.0));
    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }
  float fbm(vec2 p) {
    float s = 0.0, a = 0.5;
    for (int i = 0; i < 4; i++) { s += a * vnoise(p); p *= 2.0; a *= 0.5; }
    return s;
  }
  vec3 sFrom(vec2 uv) { return texture2D(tFrom, uv).rgb; }
  vec3 sTo(vec2 uv)   { return texture2D(tTo, uv).rgb; }

  void main() {
    float p = clamp(uProgress, 0.0, 1.0);
    float ease = smoothstep(0.0, 1.0, p);
    float bell = sin(p * PI); // 0 → 1 → 0
    vec2 uv = vUv;
    vec3 col;

    if (uType == 0) {
      // crossfade
      col = mix(sFrom(uv), sTo(uv), ease);

    } else if (uType == 1) {
      // dip to black + bloom flash
      if (p < 0.5) col = sFrom(uv) * (1.0 - p * 2.0);
      else col = sTo(uv) * (p * 2.0 - 1.0);
      col += bell * bell * 0.22 * uIntensity;

    } else if (uType == 2) {
      // luma wipe (directional) with a glowing seam
      float c = 0.5 + dot(uv - 0.5, uDir);
      float edge = 0.06 + 0.14 * uIntensity;
      float thr = mix(-edge, 1.0 + edge, p);
      float m = smoothstep(thr - edge, thr + edge, c); // 0 = revealed (to)
      col = mix(sTo(uv), sFrom(uv), m);
      float seam = exp(-pow((c - thr) / max(edge, 1e-3), 2.0) * 1.5);
      col += seam * vec3(0.6, 0.8, 1.0) * 0.5 * uIntensity;

    } else if (uType == 3) {
      // bars / blinds
      float bars = 14.0;
      vec2 perp = vec2(-uDir.y, uDir.x);
      float stripe = floor((0.5 + dot(uv - 0.5, perp)) * bars);
      float local = 0.5 + dot(uv - 0.5, uDir);
      float coord = mod(stripe, 2.0) < 1.0 ? local : 1.0 - local;
      float thr = mix(-0.25, 1.25, p);
      float m = smoothstep(thr - 0.06, thr + 0.06, coord);
      col = mix(sTo(uv), sFrom(uv), m);

    } else if (uType == 4) {
      // iris (circular reveal) with bright ring
      vec2 d = uv - 0.5; d.x *= uAspect;
      float dist = length(d) / (0.5 * sqrt(1.0 + uAspect * uAspect));
      float edge = 0.08;
      float r = mix(-edge, 1.0 + edge, p);
      float m = smoothstep(r - edge, r + edge, dist); // inside → to
      col = mix(sTo(uv), sFrom(uv), m);
      col += exp(-pow((dist - r) / edge, 2.0)) * vec3(0.7, 0.85, 1.0) * 0.4 * uIntensity;

    } else if (uType == 5) {
      // pixelate / mosaic dissolve
      float bl = max(5.0, mix(220.0, 14.0, bell * clamp(uIntensity, 0.3, 1.5)));
      vec2 muv = (floor(uv * bl) + 0.5) / bl;
      col = mix(sFrom(muv), sTo(muv), smoothstep(0.3, 0.7, p));

    } else if (uType == 6) {
      // glitch: block displacement + RGB split + scanlines + noise
      float g = bell;
      float t = uTime * 15.0 + uSeed * 50.0;
      float band = floor(uv.y * 24.0);
      float on = step(0.6, hash21(vec2(band, floor(t) + 3.0)));
      float shift = (hash21(vec2(band, floor(t))) - 0.5) * 0.18 * g * uIntensity * on;
      vec2 uvg = uv + vec2(shift, 0.0);
      float ca = (0.018 * uIntensity + 0.004) * g;
      vec3 fc = vec3(texture2D(tFrom, uvg + vec2(ca, 0.0)).r, texture2D(tFrom, uvg).g, texture2D(tFrom, uvg - vec2(ca, 0.0)).b);
      vec3 tc = vec3(texture2D(tTo, uvg + vec2(ca, 0.0)).r, texture2D(tTo, uvg).g, texture2D(tTo, uvg - vec2(ca, 0.0)).b);
      col = mix(fc, tc, smoothstep(0.35, 0.65, p));
      col *= mix(1.0, 0.85 + 0.15 * sin(uv.y * 900.0), g * 0.6);
      col += (hash21(uv * vec2(123.0, 77.0) + t) - 0.5) * 0.13 * g * uIntensity;

    } else if (uType == 7) {
      // RGB shift slide
      float g = bell;
      vec2 off = uDir * (0.06 * uIntensity * g);
      vec3 fc = vec3(texture2D(tFrom, uv + off).r, texture2D(tFrom, uv).g, texture2D(tFrom, uv - off).b);
      vec3 tc = vec3(texture2D(tTo, uv + off).r, texture2D(tTo, uv).g, texture2D(tTo, uv - off).b);
      col = mix(fc, tc, ease);

    } else if (uType == 8) {
      // zoom blur transfer
      float sf = 1.0 + p * 0.7 * uIntensity;
      float st = 1.0 + (1.0 - p) * 0.7 * uIntensity;
      vec3 fc = vec3(0.0), tc = vec3(0.0);
      for (int i = 0; i < 10; i++) {
        float k = float(i) / 9.0;
        fc += sFrom((uv - 0.5) / mix(1.0, sf, k) + 0.5);
        tc += sTo((uv - 0.5) / mix(1.0, st, k) + 0.5);
      }
      fc /= 10.0; tc /= 10.0;
      col = mix(fc, tc, smoothstep(0.35, 0.65, p));

    } else if (uType == 9) {
      // whip pan (directional motion blur smear)
      float amt = bell * 0.4 * uIntensity;
      vec3 fc = vec3(0.0), tc = vec3(0.0);
      for (int i = 0; i < 10; i++) {
        float k = float(i) / 9.0;
        fc += sFrom(uv + uDir * (amt * k) + uDir * (p * 0.5));
        tc += sTo(uv - uDir * (amt * k) + uDir * ((p - 1.0) * 0.5));
      }
      fc /= 10.0; tc /= 10.0;
      col = mix(fc, tc, smoothstep(0.4, 0.6, p));

    } else if (uType == 10) {
      // displacement melt (liquid)
      float n = fbm(uv * 3.0 + uSeed * 10.0);
      float disp = (n - 0.5) * 0.3 * uIntensity * bell;
      vec2 duv = uv + vec2(disp, disp * 0.5);
      vec3 fc = sFrom(duv + vec2(0.0, p * 0.1));
      vec3 tc = sTo(duv);
      float m = smoothstep(p - 0.12, p + 0.12, n); // n>thr → still from
      col = mix(tc, fc, m);

    } else if (uType == 11) {
      // ripple / shockwave
      vec2 d = uv - 0.5; d.x *= uAspect;
      float dist = length(d);
      float wave = sin(dist * 40.0 - p * 34.0) * 0.02 * uIntensity * (1.0 - p);
      vec2 ruv = uv + normalize(d + 1e-5) * wave;
      float r = p * 0.78;
      float m = 1.0 - smoothstep(r - 0.1, r + 0.1, dist); // inside ring → to
      col = mix(sFrom(ruv), sTo(ruv), clamp(m, 0.0, 1.0));
      col += exp(-pow((dist - r) / 0.05, 2.0)) * vec3(0.6, 0.8, 1.0) * 0.4 * uIntensity;

    } else if (uType == 12) {
      // burn (luminance-threshold dissolve with a hot edge)
      float n = fbm(uv * 4.0 + uSeed * 7.0);
      float thr = p * 1.2;
      float e = 0.12;
      float burn = smoothstep(thr - e, thr, n); // 1 = unburned (from)
      col = mix(sTo(uv), sFrom(uv), burn);
      float edge = (1.0 - burn) * smoothstep(thr - e * 1.6, thr - e, n);
      col = mix(col, vec3(1.0, 0.5, 0.12), clamp(edge, 0.0, 1.0) * uIntensity);

    } else if (uType == 13) {
      // spin blur swap
      float ang = p * PI * uIntensity;
      vec3 fc = vec3(0.0), tc = vec3(0.0);
      vec2 c = uv - 0.5; c.x *= uAspect;
      for (int i = 0; i < 10; i++) {
        float k = float(i) / 9.0;
        // Rotation/blur vanish at each layer's own end so the result is crisp:
        // from is sharp at p=0, to is sharp at p=1.
        float aF = p * ang * k;
        float aT = -(1.0 - p) * ang * k;
        float zf = 1.0 + p * 0.3;
        float zt = 1.0 + (1.0 - p) * 0.3;
        mat2 rF = mat2(cos(aF), -sin(aF), sin(aF), cos(aF));
        mat2 rT = mat2(cos(aT), -sin(aT), sin(aT), cos(aT));
        vec2 uf = rF * c * zf; uf.x /= uAspect; uf += 0.5;
        vec2 ut = rT * c * zt; ut.x /= uAspect; ut += 0.5;
        fc += sFrom(uf);
        tc += sTo(ut);
      }
      fc /= 10.0; tc /= 10.0;
      col = mix(fc, tc, smoothstep(0.35, 0.65, p));

    } else {
      col = mix(sFrom(uv), sTo(uv), ease);
    }

    gl_FragColor = vec4(col, 1.0);
  }
`

/**
 * Fullscreen compositor that mixes two live render-target textures (outgoing →
 * incoming) with a chosen transition style. Renders straight to the screen.
 * Lives outside the per-scene composer so it can sample both layers at once.
 */
export class TransitionCompositor {
  private quad: FullScreenQuad
  private material: THREE.ShaderMaterial

  constructor() {
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        tFrom: { value: null },
        tTo: { value: null },
        uProgress: { value: 0 },
        uType: { value: 0 },
        uIntensity: { value: 1 },
        uAspect: { value: 1 },
        uTime: { value: 0 },
        uSeed: { value: 0 },
        uDir: { value: new THREE.Vector2(1, 0) },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
    })
    this.quad = new FullScreenQuad(this.material)
  }

  /** Viewport aspect (width/height) — keeps circles/ripples round. */
  setAspect(aspect: number) {
    this.material.uniforms.uAspect.value = aspect
  }

  /** Fresh randomness for the procedural styles (call once per transition). */
  setSeed(seed: number) {
    this.material.uniforms.uSeed.value = seed
  }

  /** Mixes from → to by progress, drawing to the screen. */
  render(
    renderer: THREE.WebGLRenderer,
    fromTex: THREE.Texture,
    toTex: THREE.Texture,
    cfg: TransitionConfig,
    progress: number,
    time: number,
  ) {
    const u = this.material.uniforms
    u.tFrom.value = fromTex
    u.tTo.value = toTex
    u.uType.value = TX_CODE[cfg.type]
    u.uIntensity.value = cfg.intensity
    u.uProgress.value = progress
    u.uTime.value = time
    const [dx, dy] = dirVec(cfg.direction)
    ;(u.uDir.value as THREE.Vector2).set(dx, dy)
    renderer.setRenderTarget(null)
    this.quad.render(renderer)
  }

  dispose() {
    this.material.dispose()
    this.quad.dispose()
  }
}
